import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components/macro";
import { TextArea } from "../TextArea/TextArea";
import { useFormWrapper } from "../../util/util";
import { SendUpIcon } from "../Icons/Icons";

const FormRow = styled.form`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  width: 100%;
`;

const InputCell = styled.div<{ inputScrollHeight?: number }>`
  flex: 1;
  button[class*="TextArea__SendButton"] {
    transition: right 0.2s ease-out;
    right: ${({ inputScrollHeight }) =>
      inputScrollHeight && inputScrollHeight > 263 ? `18px` : "8px"} !important;
  }
  div[class*="TextArea__Wrapper"] {
    padding-right: 0px !important;
  }
`;

export type SendMessageProps = {
  onSubmit: (message: string) => void | Promise<void>;
  inProgress: boolean;
};

export function SendMessage({ onSubmit, inProgress }: SendMessageProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { register, formState, handleSubmit, setValue } = useFormWrapper({});
  const [text, setText] = useState<string>("");
  const inputEl = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    register({ name: "message", required: true });
  }, [register]);

  const submit = (data: { message: string }) => {
    const trimmed = (data.message || "").trim();
    if (!trimmed || inProgress) return;
    setValue("message", "");
    setText("");
    onSubmit(trimmed);
  };

  const hasMessage = text.trim().length > 0;
  const handleInlineSend = () => {
    submit({ message: text });
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter") return;
    if (e.shiftKey) return;
    e.preventDefault();
    if (inProgress) return;
    submit({ message: e.currentTarget.value });
  };

  return (
    <FormRow noValidate onSubmit={handleSubmit(submit)}>
      <InputCell inputScrollHeight={inputEl.current?.scrollHeight}>
        <TextArea
          name={"message"}
          label={t("Message")}
          hideFloatingLabel={true}
          placeholderText={t("Ask Virtual Chemist a question")}
          theref={inputEl}
          formState={formState}
          disabled={inProgress}
          onKeyDown={handleKeyDown}
          onChange={(e: React.FormEvent<HTMLTextAreaElement>) => {
            const v = e.currentTarget.value;
            setText(v);
            setValue("message", v);
          }}
          autoGrow
          minRows={1}
          maxRows={15}
          required={true}
          value={text}
          onSend={hasMessage ? handleInlineSend : undefined}
          sendDisabled={!hasMessage || inProgress}
          sendLoading={inProgress}
          sendIcon={
            <SendUpIcon
              width={16}
              height={16}
              stroke={theme.primaryButtonTextColor}
            />
          }
          style={{ resize: "none", paddingRight: "45px" }}
        />
      </InputCell>
    </FormRow>
  );
}
